
/// <reference path="../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />

import Dispatcher from '../frontend/dispatcher';
import { TaskListResultStatus, TaskListResult } from './TaskList';
import { IAuthenticationProvider } from './interfaces/IAuthenticationProvider';
import { LoadingActionTypes, LoadingStatus, LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage } from '../frontend/actions/LoadingActions';
import { LiveRecordingSession } from './LiveRecordingSession';
import { OneDriveUploadTask, IOneDriveUploadTaskResult } from "./OneDriveUploadTask";
import { ITranscription } from "./interfaces/ITranscriptionManager";
import { TranscriptionStorageType, IStoredTranscription } from "./interfaces/IStoredTranscription";
import * as DocumentStorage from "./office-js/DocumentStorage";
import * as AudioFormat from "./interfaces/IAudioFormat";
import { TaskList } from "./TaskList";
import { LogEvents } from "../utils/LogEvents";
import { LiveRecordingKeys } from "./telemetry/LiveRecordingKeys";
import { SetForegroundTaskInStore } from "../frontend/actions/LoadingActions";
import { IForegroundTask } from './interfaces/IForegroundTask';
import { ErrorStringFromException } from '../utils/utils';
import { ITranscriptionSourceFileProvider } from './interfaces/ITranscriptionSourceFileProvider';
import { CreateTranscriptionSourceFileProvider } from './TranscriptionSourceFileFactory';

export class LiveRecordingForegroundTask implements IForegroundTask {
    private liveRecordingSession: LiveRecordingSession;
    private history: any;
    private session: LiveRecordingSession;
    private taskList: TaskList<any> | null = null;
    private oneDriveUploadTask: OneDriveUploadTask;
    private authProvider: IAuthenticationProvider;
  
    constructor(liveRecordingSession: LiveRecordingSession, authProvider: IAuthenticationProvider, history: any) {
      this.liveRecordingSession = liveRecordingSession;
      this.history = history;
      this.authProvider = authProvider;
    }
  
    public async Execute(preauthenticatePromise: Promise<void>): Promise<void> {
        try {
            Dispatcher.dispatch({
                type: LoadingActionTypes.SetStatusAction,
                payload: LoadingStatus.UploadingFile
            });

            await preauthenticatePromise;
            await this.UploadFileAndInitiateTranscription(this.liveRecordingSession, this.history);
        } catch (e) {
            await this.CancelAndClearBackup(false /*shouldClearBackup*/)
            const failureReason = "Error uploading live recording. " + ErrorStringFromException(e);
            LogEvents.LiveRecording.End([{ key: LiveRecordingKeys.FailureReason, value: failureReason }, { key: LiveRecordingKeys.SuccessfullyEnded, value: false }]);
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: false, message: failureReason, isFinalResponse: true });
            throw e;
        }
    }

    private CleanUpTaskState() {
        this.taskList = null;
        this.oneDriveUploadTask = null;
        this.session = null;
    }

    private async CancelAndClearBackup(shouldClearBackup: boolean): Promise<void> {
        try {
            if (this.taskList)
                this.taskList.Cancel();

            // Cancel any pending onedrive upload task
            if (this.oneDriveUploadTask != null)
                await this.oneDriveUploadTask.Cancel();

            // Close the live recording connection
            if (this.session != null) {
                await this.session.Close();
                if (shouldClearBackup) {
                    await this.session.ClearBackup();
                }
    
                SetForegroundTaskInStore(null);
                LogEvents.LiveRecording.End([{ key: LiveRecordingKeys.SuccessfullyEnded, value: true }]);
            }
        
            this.CleanUpTaskState();
        } catch (e) {
            this.CleanUpTaskState();
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: false, message: `Failed when cancelling foreground task: ${ErrorStringFromException(e)}`, isFinalResponse: true });
            throw e;
        }
    }
  
    public async Cancel(): Promise<void> {
        return this.CancelAndClearBackup(true /*shouldClearBackup*/);
    }
  
    private async UploadFileAndInitiateTranscription(session: LiveRecordingSession, history: any) {      
      const uploadResult: TaskListResult<ITranscriptionSourceFileProvider> = await this.ExecuteLiveRecordingUploadTask(session, TranscriptionStrings.FolderNameToUploadAudioFiles);
      // If the upload was canceled, we are done.
      if (uploadResult.status == TaskListResultStatus.Canceled) {
        return;
      }
    
      Dispatcher.dispatch({
        type: LoadingActionTypes.SetStatusAction,
        payload: LoadingStatus.TranscribingAudio
      });
    
      await LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage(uploadResult.result, history);
    }



    // Executes the upload tasks. This covers recording file upload, emptying browser-local cache and writing stored transcription to the doc.
    private async ExecuteLiveRecordingUploadTask(session: LiveRecordingSession, folderName: string)
        : Promise<TaskListResult<ITranscriptionSourceFileProvider>> {
        this.session = session;
        this.taskList = new TaskList<ITranscriptionSourceFileProvider | AudioFormat.IAudioFile | IOneDriveUploadTaskResult
        | {transcription: ITranscription, result: IOneDriveUploadTaskResult} | IStoredTranscription | void>([
            // Pause the live recording
            async (): Promise<void> =>
                session.Pause(),

            // Get the audio file recording
            async (): Promise<AudioFormat.IAudioFile> =>
                session.GetRecording(),

            // Log the duration of the audio file and upload to OneDrive
            async (audioFile: AudioFormat.IAudioFile): Promise<IOneDriveUploadTaskResult> => {
                LogEvents.LiveRecording.AddData([{ key: LiveRecordingKeys.AudioLengthSeconds, value: audioFile.duration }]);
                this.oneDriveUploadTask = new OneDriveUploadTask(this.authProvider, folderName, audioFile.file);
                return this.oneDriveUploadTask.UploadFile();
            },

            // Get the transcription
            async (result: IOneDriveUploadTaskResult): Promise<{transcription: ITranscription, result: IOneDriveUploadTaskResult}> => {
                const transcription = await session.GetTranscript();
                return { transcription, result };
            },

            // Close the live recording session
            async (transcriptionAndResult: {transcription: ITranscription, result: IOneDriveUploadTaskResult}): Promise<IStoredTranscription> => {
                await session.Close();
                await session.ClearBackup();
                SetForegroundTaskInStore(null);
                this.session = session = null;

                LogEvents.LiveRecording.End([{ key: LiveRecordingKeys.SuccessfullyEnded, value: true }]);
                LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: null, isFinalResponse: true});

                return {
                    storageType: TranscriptionStorageType.DocumentStorage,
                    descriptor:  {
                        transcription: transcriptionAndResult.transcription,
                        audioOneDriveItem: transcriptionAndResult.result.oneDriveItem,
                    }
                };
            },

            // Write stored transcription to document storage.
            async (storedTranscription: IStoredTranscription): Promise<ITranscriptionSourceFileProvider> => {
                await DocumentStorage.WriteStoredTranscription(storedTranscription);
                return CreateTranscriptionSourceFileProvider(storedTranscription, this.authProvider);
            }
        ]);

        const result = await this.taskList.ExecuteTaskListSequentially();
        this.CleanUpTaskState();
        return <TaskListResult<ITranscriptionSourceFileProvider>>result;
    }
  }